import { Component, OnInit, ChangeDetectorRef, OnDestroy, effect, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { Observable, of, Subscription } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { HardwareVariant, HardwareVariantGroup } from '../../models/mobilephone-variant';
import { MobilePhoneSearchItem } from '../../models/mobilephone-search-item';
import { AddMobilePhoneReplacementAction } from '@sales/states/customer-product-mobilephone/customer-product-mobilephone.actions';
import { SearchDisplayType } from '../../models/mobilephone-search-enums';
import {
  ApiMobilephoneSearchAction,
  ApiMobilephoneFiltersAction,
} from '../../states/mobilephone-search/mobilephone-search.actions';
import { IconNames } from '@shared/enums/icon-names';
import { NotificationService } from '@core/services/notification.service';
import {
  DamageDetails,
  CustomerEquipmentUpgradeAgreement,
  MobileReturnMethod,
} from '../../models/equipment-upgrade-agreement.model';
import { EquipmentService } from '@sales/services/equipment.service';
import { catchError, map } from 'rxjs/operators';
import { MobilBytteSelectors } from '../../states/mobilbytte/mobilbytte.selectors';
import { GradingQuestion, InsuranceEligibility } from '../../models/equipment-api.model';
import {
  SetClaimIdOnEquipmentUpgradeAgreementAction,
  SetMobileInsuranceAndReturnDetailsAction,
  SetMobileDamageDetailsAction,
} from '../../states/mobilbytte/mobilbytte.actions';
import { CustomerSelectors } from '@sales/modules/details/states/customer.selectors';
import { MobilePhoneDetails } from '../mobilephone-search/mobilephone-details/mobilephone-details.component';
import { ThemeService } from '@core/services/theme.service';
import { Theme } from '@support/models/theme.model';
import { MobilephoneSearchService } from '../../services/mobilephone-search.service';
import { MobilephoneSearchHelperService } from '../../services/mobilephone-search-helper.service';
import { MobilePhoneSearchSelectors } from '../../states/mobilephone-search/mobilephone-search.selectors';
import { AsyncPipe } from '@angular/common';
import { MobilephoneMobilbytteBytteDetailsComponent } from './mobilephone-mobilbytte-bytte-details/mobilephone-mobilbytte-bytte-details.component';
import { HardwareSearchResultComponent } from '@app/shared/components/hardware-search-result/hardware-search-result.component';
import { MobilephoneSearchFilterComponent } from '../mobilephone-search/mobilephone-search-filter/mobilephone-search-filter.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MobilephoneSearchHeaderComponent } from '../mobilephone-search/mobilephone-search-header/mobilephone-search-header.component';

enum Messages {
  RetrieveGradingQuestionsFailed = 'Feil ved henting av spørsmål for tilstandssjekk. Prøv på nytt senere.',
  ErrorOnFetchingMobileDetails = 'Feil ved henting av mobildetaljer. Prøv på nytt senere.',
}

//This component is used purely for EquipmentUpgradeUpgrade
//when choosing a new mobile phone to upgrade to
@Component({
  selector: 'app-mobilephone-mobilbytte-bytte-search',
  templateUrl: './mobilephone-mobilbytte-bytte-search.component.html',
  styleUrls: ['./mobilephone-mobilbytte-bytte-search.component.scss'],
  imports: [
    MobilephoneSearchHeaderComponent,
    CdkScrollable,
    MatDialogContent,
    MobilephoneSearchFilterComponent,
    HardwareSearchResultComponent,
    MobilephoneMobilbytteBytteDetailsComponent,
    AsyncPipe,
  ],
})
export class MobilephoneMobilbytteBytteSearchComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  dialogRef = inject<MatDialogRef<MobilephoneMobilbytteBytteSearchComponent>>(MatDialogRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private equipmentService = inject(EquipmentService);
  private notificationService = inject(NotificationService);
  private mobilePhoneSearchService = inject(MobilephoneSearchService);
  private mobilePhoneSearchHelperService = inject(MobilephoneSearchHelperService);
  private themeService = inject(ThemeService);
  data = inject<{
    equipmentUpgradeAgreementId: string;
    isManualRegistration: boolean;
  }>(MAT_DIALOG_DATA);

  mobilephoneSearch = select(MobilePhoneSearchSelectors.getState);
  existingEquipmentUpgradeAgreements = select(MobilBytteSelectors.existingEquipmentUpgradeAgreements);
  customerDetails = select(CustomerSelectors.customerDetails);

  gradingQuestions$: Observable<GradingQuestion[]>;

  selectedMobilePhoneVariant: HardwareVariant = null;
  selectedMobilePhoneVariants: HardwareVariant[] = [];
  allMobilePhoneVariantGroups: HardwareVariantGroup[] = [];
  filteredMobilePhoneVariantGroups: HardwareVariantGroup[];

  displayType = SearchDisplayType.Grid;
  searchText: string = '';
  selectedManufacturers: string[] = [];
  isOldMobileSelected: boolean = false;
  searchResults: MobilePhoneSearchItem[];
  isLoadingSearchResults: boolean = false;
  IconNames = IconNames;

  manufacturers: string[] = [];
  isLoadingFilters: boolean = false;
  equipmentUpgradeAgreement: CustomerEquipmentUpgradeAgreement;
  selectedTheme$: Observable<Theme>;

  subscriptions$: Subscription[] = [];

  constructor() {
    effect(() => {
      if (this.mobilephoneSearch()) {
        this.allMobilePhoneVariantGroups = this.mobilephoneSearch().searchResults.filter(
          (group: HardwareVariantGroup) => group.hasMobilBytte
        );
        this.isLoadingSearchResults = this.mobilephoneSearch().isLoadingSearchResults;
        this.manufacturers = this.mobilephoneSearch().mobilePhoneFilters?.manufacturers || [];
        this.isLoadingFilters = this.mobilephoneSearch().isLoadingMobilephoneFilters;
        this.changeDetectorRef.detectChanges();
      }
    });

    effect(() => {
      this.equipmentUpgradeAgreement = this.existingEquipmentUpgradeAgreements().find(
        a => a.agreementId === this.data.equipmentUpgradeAgreementId
      );
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnInit() {
    this.selectedTheme$ = this.themeService.selectedTheme;
    this.store.dispatch([new ApiMobilephoneSearchAction(), new ApiMobilephoneFiltersAction()]);

    this.gradingQuestions$ = this.equipmentService.getGradingQuestions().pipe(
      map((gradingQuestions: GradingQuestion[]) =>
        gradingQuestions.map((question: GradingQuestion) => ({
          ...question,
          isCollapsed: true,
        }))
      ),
      catchError(() => {
        this.notificationService.error(Messages.RetrieveGradingQuestionsFailed);
        return of([]);
      })
    );
  }

  get mobilePhoneGroupsCount(): number {
    return this.allMobilePhoneVariantGroups?.length;
  }

  selectMobilephone(selected: HardwareVariant) {
    this.subscriptions$.push(
      this.mobilePhoneSearchService.getMobilePhoneVariants(selected?.groupSlug).subscribe(
        mobileVariants => {
          this.selectedMobilePhoneVariants = mobileVariants;
          this.selectedMobilePhoneVariant ||=
            this.mobilePhoneSearchHelperService.getCheapestVariantAvailableForPurchaseFromGroupVariants(
              this.selectedMobilePhoneVariants
            );
          this.changeDetectorRef.detectChanges();
        },
        () => {
          this.notificationService.error(Messages.ErrorOnFetchingMobileDetails);
        }
      )
    );
  }

  changeDisplay(value: SearchDisplayType) {
    this.displayType = value;
  }

  onFilterChange(formValue: any) {
    this.searchText = formValue.searchText;
    this.selectedManufacturers = formValue.manufacturers;

    const hasSearchText = this.searchText.length > 0;
    const hasSelectedManufacturers = this.selectedManufacturers.length > 0;

    const searchTextFilter = x => (hasSearchText ? x.name.toLowerCase().includes(this.searchText.toLowerCase()) : true);
    const manufacturerFilter = x => (hasSelectedManufacturers ? this.selectedManufacturers.includes(x.manufacturer) : true);

    if (!hasSearchText && !hasSelectedManufacturers) {
      this.filteredMobilePhoneVariantGroups = null;
    } else {
      const filtered = this.allMobilePhoneVariantGroups.filter(m => searchTextFilter(m) && manufacturerFilter(m));

      this.filteredMobilePhoneVariantGroups = filtered;
    }
  }

  get mobilePhoneVariantGroupsToDisplay(): HardwareVariantGroup[] {
    return this.filteredMobilePhoneVariantGroups || this.allMobilePhoneVariantGroups;
  }

  onGradingCompleted({
    newMobilePhoneVariant,
    mobilePhoneDetails,
    redemptionId,
    useInsurance,
    claimId,
    damageDetails,
    mobileReturnMethod,
    insuranceEligibility,
  }: {
    newMobilePhoneVariant: HardwareVariant;
    mobilePhoneDetails: MobilePhoneDetails;
    redemptionId: number;
    useInsurance: boolean;
    claimId: string;
    damageDetails: DamageDetails;
    mobileReturnMethod: MobileReturnMethod;
    insuranceEligibility: InsuranceEligibility;
  }) {
    this.store.dispatch(
      new SetMobileInsuranceAndReturnDetailsAction(
        this.equipmentUpgradeAgreement.agreementId,
        redemptionId,
        insuranceEligibility,
        mobileReturnMethod
      )
    );

    if (useInsurance) {
      this.store.dispatch(new SetMobileDamageDetailsAction(this.equipmentUpgradeAgreement.agreementId, damageDetails));

      if (claimId) {
        this.store.dispatch(
          new SetClaimIdOnEquipmentUpgradeAgreementAction(this.equipmentUpgradeAgreement.agreementId, claimId)
        );
      }
    }
    this.store.dispatch(
      new AddMobilePhoneReplacementAction(
        newMobilePhoneVariant,
        mobilePhoneDetails,
        this.equipmentUpgradeAgreement.agreementId,
        this.data.isManualRegistration
      )
    );
    this.dialogRef.close();
  }

  backToSearch() {
    this.selectedMobilePhoneVariant = null;
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
